import classnames from 'classnames'
import { css } from 'emotion'
import { get } from 'lodash'
import * as React from 'react'

import { PersonalizedStatuses } from 'embed/constants'
import { formatImageUrl } from 'embed/utils'
import { CreditOffer, CreditOfferDetails } from 'embed/constants/models'

import Modal from 'embed/components/Modal'
import PersonalizedStatusExplanation from 'embed/components/PersonalizedStatusExplanation'
import SpecialSubtypeNotice, { CreditCardSubtypeData } from 'embed/components/SpecialSubtypeNotice'
import ThemeConsumer from 'embed/components/ThemeConsumer'

import { OfferFlag } from '../CreditOfferList/OfferFlag'

const styles = require('./styles.scss')

interface Props {
  callToAction?: React.ReactNode
  offer: CreditOffer
  offset?: number
  onDismiss: () => void
}

export const CreditModal = ({ callToAction, offer, offset, onDismiss }: Props) => {
  const { details, partner } = offer

  // Additional Details
  const additionalDetailsListItems =
    details.additionalDetails !== undefined
      ? details.additionalDetails.map((value, key) => <li dangerouslySetInnerHTML={{ __html: value }} key={`${key}`} />)
      : null

  function getPersonalizedStatusFlag() {
    let personalizedStatus
    PersonalizedStatuses.some((key) => {
      const value = details[key]
      if (value) {
        personalizedStatus = key
      }
      return value
    })
    return personalizedStatus
  }

  // Special subtype notice
  const showSpecialSubtypeNotice = CreditCardSubtypeData[offer.productSubType ?? ''] !== undefined

  // Card Name
  const cardName = details.cardName

  // Card Image
  const cardImage = <img className={styles.cardImage} alt={cardName} src={formatImageUrl(details.cardImageUrl)} />

  // Issuer Name
  const issuerName = offer.partner?.name

  // Rates URL
  const ratesUrls = details.ratesUrl

  // Offer Details
  const detailsListItems =
    details.details !== undefined ? details.details.map((value, key) => <li key={`${key}`}>{`${value}`}</li>) : null

  return (
    <Modal
      className={classnames({
        [styles.modalContainer]: details.preApproved || details.preQualified || details.preSelected,
      })}
      offset={offset}
      onDismiss={onDismiss}
    >
      <ThemeConsumer>
        {({ colorPrimary, colorTextSecondary, fontFamilyPrimary }) => (
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <div className={styles.modalCard}>
                <OfferFlag offer={offer} />
                <div className={styles.modalCardImage}>{cardImage}</div>
                <div className={styles.modalCardDetails}>
                  <div className={styles.modalCardName}>{cardName}</div>
                  <div
                    className={classnames(
                      styles.modalIssuerName,
                      css`
                        color: ${colorTextSecondary};
                      `,
                    )}
                  >
                    By {issuerName}
                  </div>
                </div>
              </div>
              {callToAction && <div className={styles.modalCallToAction}>{callToAction}</div>}
            </div>
            {detailsListItems && (
              <div className={styles.offerDetails}>
                <h4
                  className={css`
                    font-family: ${fontFamilyPrimary};
                  `}
                >
                  Description
                </h4>
                <ul className={styles.modalCardDetailsItems}>{detailsListItems}</ul>
              </div>
            )}
            {showSpecialSubtypeNotice && (
              <div>
                <h4
                  className={css`
                    font-family: ${fontFamilyPrimary};
                  `}
                >
                  <SpecialSubtypeNotice subtype={offer.productSubType} />
                </h4>
                {offer.productSubType && <p>{CreditCardSubtypeData[offer.productSubType].details}</p>}
              </div>
            )}
            {additionalDetailsListItems && (
              <div className={styles.offerDetails}>
                <h4
                  className={css`
                    font-family: ${fontFamilyPrimary};
                  `}
                >
                  Details
                </h4>
                <ul>{additionalDetailsListItems}</ul>
              </div>
            )}
            <PersonalizedStatusExplanation
              fontFamily={fontFamilyPrimary}
              personalizedStatus={getPersonalizedStatusFlag()}
            />
            <div>
              <a
                className={css`
                  color: ${colorPrimary};
                `}
                href={ratesUrls}
                target="_blank"
              >
                See all rates &amp; terms
              </a>
            </div>
          </div>
        )}
      </ThemeConsumer>
    </Modal>
  )
}

CreditModal.defaultProps = {
  offset: 0,
}
